/**
 * Turns the daily figures of the daily sales progress into the three cumulative lines the burnup draws:
 * facturado (invoices, up to today), proyectado (pending orders, from today on) and the directriz
 * (budget prorated across the period). One instance per unit: each one knows which fields of a
 * daily entry hold its values, so money and boxes accumulate through the very same logic.
 */
export default class CumulativeBurnupSeries {
    static money() {
        return new CumulativeBurnupSeries({
            invoicedOf: (dailyEntry) => dailyEntry.invoiced,
            ordersOf: (dailyEntry) => dailyEntry.orders,
            budgetOf: (dailyEntry) => dailyEntry.budget,
            proratedBudgetOf: (dailyEntry) => dailyEntry.proratedBudget,
        });
    }

    static boxes() {
        return new CumulativeBurnupSeries({
            invoicedOf: (dailyEntry) => dailyEntry.invoicedBoxes,
            ordersOf: (dailyEntry) => dailyEntry.ordersBoxes,
            budgetOf: (dailyEntry) => dailyEntry.budgetBoxes,
            proratedBudgetOf: (dailyEntry) => dailyEntry.proratedBudgetBoxes,
        });
    }

    constructor({ invoicedOf, ordersOf, budgetOf, proratedBudgetOf }) {
        this._invoicedOf = invoicedOf;
        this._ordersOf = ordersOf;
        this._budgetOf = budgetOf;
        this._proratedBudgetOf = proratedBudgetOf;
    }

    pointsFor(dailyEntries, today) {
        const monthlyBudgetByMonth = this._monthlyBudgetByMonth(dailyEntries);

        let invoicedCumulative = 0;
        let projectedCumulative = 0;
        let completedMonthsBudget = 0;
        let currentMonth = null;

        return dailyEntries.map((dailyEntry) => {
            const date = dailyEntry.date;
            const month = this._monthOf(date);
            const aMonthWasCompleted = currentMonth !== null && month !== currentMonth;
            if (aMonthWasCompleted) {
                completedMonthsBudget += monthlyBudgetByMonth[currentMonth];
            }
            currentMonth = month;

            const isPast = !date.isAfter(today);
            let invoiced = null;
            let projected = null;
            if (isPast) {
                invoicedCumulative += this._invoicedOf(dailyEntry) || 0;
                projectedCumulative = invoicedCumulative;
                invoiced = invoicedCumulative;
                if (date.isSame(today, "day")) {
                    projected = projectedCumulative;
                }
            } else {
                projectedCumulative += this._ordersOf(dailyEntry) || 0;
                projected = projectedCumulative;
            }

            const proratedBudget = this._proratedBudgetOf(dailyEntry);
            const target = proratedBudget != null ? completedMonthsBudget + proratedBudget : null;

            return { invoiced, projected, target };
        });
    }

    _monthlyBudgetByMonth(dailyEntries) {
        const monthlyBudgetByMonth = {};
        dailyEntries.forEach((dailyEntry) => {
            const month = this._monthOf(dailyEntry.date);
            if (!(month in monthlyBudgetByMonth)) {
                monthlyBudgetByMonth[month] = this._budgetOf(dailyEntry) || 0;
            }
        });
        return monthlyBudgetByMonth;
    }

    _monthOf(date) {
        return `${date.year()}-${date.month()}`;
    }
}
